import React, { useEffect, useState } from "react";
import { Box, styled, Tooltip } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AutocompleteField from "controls/global/custom-autocomplete/AutocompleteField";
import { colors } from "theme/defaultStyle";
import SelectPreferredUnderwriterDialog from "./select-preferred-underwriter/SelectPreferredUnderwriterDialog";
import useFormWrapper from "utils/custom-hooks/useFormWrapper";
import { useLookup } from "utils/context/LookupContext";
import { useWatch } from "react-hook-form";

export interface UnderwriterItem {
  uniqueIdentifier: string;
  lastName: string;
  firstName: string;
  email: string;
  userID: string;
  fullName?: string;
}

interface PreferredUnderwriterFieldProps {
  name: string;
  disabled?: boolean;
  onChange?: () => void;
  schema: string;
}

type APIPreferredUnderwriterItem = {
  StateAbbr: string;
  UWAGroupTypeCode: string;
  IndentityKey: number;
  UserID: string;
  FirstName: string;
  LastName: string;
  UserName: string;
  EmailAddress: string;
  UWAUserTypeCode: string;
  UWAUserTypeName: string;
  UWAApprovalLevelTypeCode: string;
  UWAApprovalLevelTypeName: string;
};

const FieldWrapper = styled(Box)({
  position: "relative",
  display: "flex",
  alignItems: "center",
  width: "100%",
  overflow: "visible",
  paddingTop: "1px",
  paddingBottom: "1px",
});

const StyledSearchIcon = styled(SearchIcon)({
  color: colors.blue01,
  fontSize: "28px",
  cursor: "pointer",
  position: "absolute",
  right: "12px",
  top: "50%",
  transform: "translateY(-50%)",
  zIndex: 2,
  pointerEvents: "auto",
  flexShrink: 0,
  "&:hover": {
    opacity: 0.8,
  },
  "&.disabledIconButton": {
    opacity: 0.5,
    cursor: "not-allowed",
    pointerEvents: "none",
  },
});

const PreferredUnderwriterField = ({
  schema,
  name,
  disabled = false,
  onChange,
}: PreferredUnderwriterFieldProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { setValue } = useFormWrapper();
  const [preferredUnderwriters, setPreferredUnderwriters] = useState<
    UnderwriterItem[]
  >([]);
  const [selectedUnderwriter, setSelectedUnderwriter] =
    useState<UnderwriterItem | null>(null);

  const [propertyState] = useWatch({
    name: ["properties.0.state.abbr"],
  });

  const [, { getPreferredUnderwriters }] = useLookup();

  const handleSubmit = () => {
    setValue(
      `${schema}.preferredUnderwriterName`,
      `${selectedUnderwriter?.firstName || ""} ${selectedUnderwriter?.lastName || ""
      }`
    );
    setValue(
      `${schema}.preferredUnderwriter`,
      selectedUnderwriter?.uniqueIdentifier || ""
    );
    setIsDialogOpen(false)
  };

  useEffect(() => {
    if(!disabled){
      setValue(name, "");
      setValue(`${schema}.preferredUnderwriter`, null);
    }
  }, [propertyState, disabled, name, schema, setValue]);

  useEffect(() => {
    const fetchPreferredUnderwriters = async () => {
      try {
        const { apiResponse }: { apiResponse: APIPreferredUnderwriterItem[] } =
          await getPreferredUnderwriters(propertyState);

        const mappedUnderwriters: UnderwriterItem[] =
          apiResponse?.map((uw) => ({
            uniqueIdentifier: uw.UserID || "",
            lastName: uw.LastName || "",
            firstName: uw.FirstName || "",
            fullName: `${uw.FirstName || ""} ${uw.LastName || ""}`.trim(),
            email: uw.EmailAddress || "",
            userID: uw.UserID || "",
          })) || [];

        setPreferredUnderwriters(mappedUnderwriters);
      } catch (err) {
        setPreferredUnderwriters([]);
      }
    };
    fetchPreferredUnderwriters();

    // console.log("Preferred Underwriters:", preferredUnderwriters);
  }, [propertyState]);

  const onGetUnderwriters = async (
    newInputValue: string
  ): Promise<UnderwriterItem[]> => {
    if (!newInputValue || !newInputValue.length) {
      return [];
    }

    const searchTerm = newInputValue.toLowerCase();

    const filteredUnderwriters = preferredUnderwriters?.filter((underwriter) =>
      underwriter.fullName?.toLowerCase().includes(searchTerm)
    );

    return filteredUnderwriters;
  };

  const onUnderwriterSelected = (newValue: UnderwriterItem) => {
    let valueToSave: string;

    if (typeof newValue === "string") {
      valueToSave = newValue;
    } else if (newValue) {
      valueToSave =
        newValue.fullName || `${newValue.firstName} ${newValue.lastName}`;
    } else {
      valueToSave = "";
    }
    setValue(name, valueToSave);
    setValue(`${schema}.preferredUnderwriter`, newValue.userID);

    if (onChange) {
      onChange();
    }
  };

  const handleSearchIconClick = () => {
    if (!disabled) {
      setIsDialogOpen(true);
    }
  };

  const getOptionSelected = (
    option: UnderwriterItem,
    value: UnderwriterItem
  ) => {
    return option.uniqueIdentifier === value.uniqueIdentifier;
  };

  const getOptionLabel = (option: any) => {
    if (typeof option === "string") {
      return option;
    }
    return option.fullName || `${option.firstName} ${option.lastName}`;
  };

  const renderOption = (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: UnderwriterItem
  ) => {
    return (
      <Box component="li" {...props}>
        {option.firstName} {option.lastName}
      </Box>
    );
  };

  return (
    <>
      <FieldWrapper>
        <Box sx={{ 
          flex: 1, 
          position: "relative",
          width: "100%",
          "& .MuiOutlinedInput-root": {
            paddingRight: "48px",
            backgroundColor: `${colors.white}`,
            overflow: "hidden",
          },
          "& .MuiInputBase-root": {
            paddingRight: "48px !important",
            backgroundColor: `${colors.white}`,
          },
          "& .MuiOutlinedInput-notchedOutline": {
             top: "-4px",
          },
          "& .MuiInputLabel-outlined": {
            "&:not(.MuiInputLabel-shrink)": {
              transform: "translate(14px, 16px) scale(1)",
            },
          },
        }}>
          <AutocompleteField
            debounceTime={300}
            freeSolo={false}
            label="Preferred Underwriter"
            name={name}
            disabled={disabled}
            onGetOptions={onGetUnderwriters}
            onValueChanged={onUnderwriterSelected}
            renderOption={renderOption}
            getOptionLabel={getOptionLabel}
            getOptionSelected={getOptionSelected}
            autoSelectOnExactMatch={true}
          />
          <Tooltip
            title="Search Underwriters"
            arrow
          >
            <StyledSearchIcon
              onClick={handleSearchIconClick}
              className={disabled ? "disabledIconButton" : ""}
            />
          </Tooltip>
        </Box>
      </FieldWrapper>
      {isDialogOpen && (
        <SelectPreferredUnderwriterDialog
          schema={schema}
          isOpen={isDialogOpen}
          onClose={() => setIsDialogOpen(false)}
          preferredUnderwriters={preferredUnderwriters}
          selectedUnderwriter={selectedUnderwriter}
          setSelectedUnderwriter={setSelectedUnderwriter}
          handleSubmit={handleSubmit}
        />
      )}
    </>
  );
};

export default PreferredUnderwriterField;
